import { useContext } from "react"

// Navigation
import { matchPath, useLocation } from "@/lib/router"
import { Routes } from "@/constants/routes"
import { Link } from "@/components/Anchor"

// Translation
import { useTrans } from "@/i18n"

// UI
import { cn } from "@/lib/utils"
import { NavItem } from "../NavItem"

// Icons
import { UserIcon } from "@/lib/icons"

// Context
import { AuthLayoutContext } from "@/layouts/AuthLayout"
import { useRequiredStepsBeforePendingInvestment } from "@/context/requiredStepsBeforePendingInvestment"
import { useCurrentHasValidBankDetails } from "@/context/user"

/**
 * ProfileSubMenu
 * @returns
 */
export function ProfileSubMenu() {
	// Router
	const location = useLocation()

	// Context
	const { setMobileMenuOpen } = useContext(AuthLayoutContext)
	const { amountOfRequiredActionsLeft } =
		useRequiredStepsBeforePendingInvestment()
	const hasValidBankDetails = useCurrentHasValidBankDetails()

	/**
	 * Test if location path is any child page of the investor profile
	 * @returns
	 */
	function isInvestorProfileSpecificSubPath() {
		// Return true if it is a full match
		if (location.pathname === Routes.InvestorProfile) {
			return true
		}

		// Check if the current path is a subpath of the ProjectsDashboard page
		const matchesPath = matchPath(
			{
				path: "/investor/:tab",
				end: true,
			},
			location.pathname,
		)
		return matchesPath !== null
	}

	function isPersonalDetailsSpecificSubPath() {
		// Return true if it is a full match
		if (location.pathname === Routes.SettingsProfilePersonalDetails) {
			return true
		}

		// Check if the current path is a subpath of the PersonalDetails page
		const matchesPath = matchPath(
			{
				path: "/settings/profile/:tab",
				end: true,
			},
			location.pathname,
		)
		return matchesPath !== null
	}

	const t = useTrans(["common", "investments"])

	function closeMenu() {
		setMobileMenuOpen(false)
	}

	return (
		<NavItem
			name={t("common:navigation.investments.profile")}
			data-testid="common:nav.profile"
			icon={UserIcon}
		>
			<nav className="space-y-1 pr-3">
				{/** Personal data */}
				<Link
					href={Routes.SettingsProfilePersonalDetails}
					className="flex w-full items-center focus:no-underline"
					onClick={closeMenu}
				>
					<>
						<span
							className={cn(
								isPersonalDetailsSpecificSubPath() &&
									"active-navigation-item",
								"block truncate text-sm hover:text-gray-500",
							)}
						>
							{t("common:layout.quick_menu.your_profile")}
						</span>

						{/** Show badge with number */}
						{hasValidBankDetails === false ? (
							<span className="ml-1 block size-4 items-center rounded-full bg-red-600 text-center text-xs text-white">
								{" "}
								{1}
							</span>
						) : null}
					</>
				</Link>

				{/** Investor profile */}
				<Link
					className="flex w-full items-center focus:no-underline"
					href={Routes.InvestorProfile}
					onClick={closeMenu}
				>
					<>
						<span
							className={cn(
								isInvestorProfileSpecificSubPath() &&
									"active-navigation-item",
								"block truncate text-sm hover:text-gray-500",
							)}
						>
							{t("common:navigation.investor-profile.title")}
						</span>

						{/** Show badge with number */}
						{amountOfRequiredActionsLeft !== undefined &&
							amountOfRequiredActionsLeft !== 0 && (
								<span className="ml-1 block size-4 items-center rounded-full bg-red-600 text-center text-xs text-white">
									{" "}
									{amountOfRequiredActionsLeft}
								</span>
							)}
					</>
				</Link>

				{/** Communication hub */}
				<Link
					className={({ isActive }) =>
						cn(
							isActive && "active-navigation-item",
							"block truncate text-sm hover:text-gray-500",
						)
					}
					href={Routes.Communications}
					onClick={closeMenu}
				>
					{t("common:nav.all_messages")}
				</Link>
			</nav>
		</NavItem>
	)
}
